import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { ECallRecordingType } from 'src/enums/ECallRecording';

interface AudioPlayerContextType {
	isPlaying: boolean;
	currentAudioUrl: string | null;
	currentTime: number;
	duration: number;
	audioSourceInfo: string | null;
	currentRecord: any | null;
	volume: number;
	setVolume: (volume: number) => void;
	playbackSpeed: number;
	setPlaybackSpeed: (speed: number) => void;
	playAudio: (
		url: string,
		record: any,
		type: ECallRecordingType.CALL | ECallRecordingType.VOICEMAIL,
		customDuration?: number,
		startTime?: number,
	) => void;
	pauseAudio: () => void;
	stopAudio: () => void;
	seekTo: (time: number) => void;
	type: ECallRecordingType.CALL | ECallRecordingType.VOICEMAIL;
	downloadAudio: () => void;
}

const AudioPlayerContext = createContext<AudioPlayerContextType | undefined>(undefined);

export const AudioPlayerProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
	const [audioElement, setAudioElement] = useState<HTMLAudioElement | null>(null);
	const [isPlaying, setIsPlaying] = useState(false);
	const [currentAudioUrl, setCurrentAudioUrl] = useState<string | null>(null);
	const [currentTime, setCurrentTime] = useState(0);
	const [duration, setDuration] = useState(0);
	const [audioSourceInfo, setAudioSourceInfo] = useState<string | null>(null);
	const [currentRecord, setCurrentRecord] = useState<any | null>(null);
	const [type, setType] = useState<ECallRecordingType.CALL | ECallRecordingType.VOICEMAIL>(
		ECallRecordingType.CALL,
	);
	const [volume, setVolumeState] = useState(1);
	const [playbackSpeed, setPlaybackSpeedState] = useState(1);

	const setVolume = (newVolume: number) => {
		setVolumeState(newVolume);
		if (audioElement) {
			audioElement.volume = newVolume;
		}
	};

	const setPlaybackSpeed = (speed: number) => {
		setPlaybackSpeedState(speed);
		if (audioElement) {
			audioElement.playbackRate = speed;
		}
	};

	useEffect(() => {
		if (audioElement) {
			audioElement.volume = volume;
			audioElement.playbackRate = playbackSpeed;
		}
	}, [audioElement, volume, playbackSpeed]);

	useEffect(() => {
		if (!audioElement) {
			const audio = new Audio();
			audio.controls = true;
			audio.style.display = 'none'; // Hide the default controls
			document.body.appendChild(audio);
			setAudioElement(audio);
		}

		return () => {
			if (audioElement) {
				document.body.removeChild(audioElement);
			}
		};
	}, []);

	useEffect(() => {
		if (audioElement) {
			const handleTimeUpdate = () => {
				if (currentRecord?.voicemailRecordStarted && currentRecord?.voicemailRecordDuration) {
					// For voicemails, show time relative to voicemail start
					const relativeTime = audioElement.currentTime - currentRecord.voicemailRecordStarted;
					setCurrentTime(relativeTime);

					// Stop if reached end of voicemail
					if (relativeTime >= currentRecord.voicemailRecordDuration) {
						audioElement.pause();
						setIsPlaying(false);
						setCurrentTime(0);
						// Reset audio position to start of voicemail
						audioElement.currentTime = currentRecord.voicemailRecordStarted;
					}
				} else {
					setCurrentTime(audioElement.currentTime);
				}
			};
			const handleLoadedMetadata = () => {
				if (currentRecord?.voicemailRecordDuration) {
					// For voicemails, use the voicemail duration
					setDuration(currentRecord.voicemailRecordDuration);
				} else {
					setDuration(audioElement.duration);
				}
			};
			const handleEnded = () => {
				setIsPlaying(false);
				setCurrentTime(0);
				// Reset audio position for both voicemail and call recordings
				if (currentRecord?.voicemailRecordStarted) {
					audioElement.currentTime = currentRecord.voicemailRecordStarted;
				} else {
					audioElement.currentTime = 0;
				}
			};

			const handlePlay = () => setIsPlaying(true);
			const handlePause = () => setIsPlaying(false);

			audioElement.addEventListener('timeupdate', handleTimeUpdate);
			audioElement.addEventListener('loadedmetadata', handleLoadedMetadata);
			audioElement.addEventListener('ended', handleEnded);
			audioElement.addEventListener('play', handlePlay);
			audioElement.addEventListener('pause', handlePause);

			return () => {
				audioElement.removeEventListener('timeupdate', handleTimeUpdate);
				audioElement.removeEventListener('loadedmetadata', handleLoadedMetadata);
				audioElement.removeEventListener('ended', handleEnded);
				audioElement.removeEventListener('play', handlePlay);
				audioElement.removeEventListener('pause', handlePause);
			};
		}
	}, [audioElement, currentRecord]);

	const playAudio = (
		url: string,
		record: any,
		type: ECallRecordingType.CALL | ECallRecordingType.VOICEMAIL,
	) => {
		if (!audioElement) {
			const audio = new Audio();
			setAudioElement(audio);
		}

		if (currentAudioUrl !== url) {
			audioElement!.src = url;
			setCurrentAudioUrl(url);
			setCurrentRecord(record);
			setType(type);
			const sourceInfo = `${type === ECallRecordingType.VOICEMAIL ? 'Voicemail' : 'Call Recording'}`;
			setAudioSourceInfo(sourceInfo);

			// Set duration immediately for voicemails
			if (type === ECallRecordingType.VOICEMAIL && record?.voicemailRecordDuration) {
				setDuration(record.voicemailRecordDuration);
			}
		}

		// Set initial position for voicemails
		if (type === ECallRecordingType.VOICEMAIL && record?.voicemailRecordStarted) {
			audioElement!.currentTime = record.voicemailRecordStarted;
			setCurrentTime(0);
		}

		audioElement!
			.play()
			.then(() => {
				setIsPlaying(true);
			})
			.catch((error) => {
				console.error('Error playing audio:', error);
			});
	};

	const pauseAudio = () => {
		if (audioElement) {
			audioElement.pause();
			setIsPlaying(false);
		}
	};

	const stopAudio = () => {
		if (audioElement) {
			audioElement.pause();
			audioElement.currentTime = 0;
			setIsPlaying(false);
			setCurrentAudioUrl(null);
			setCurrentTime(0);
			setAudioSourceInfo(null);
			setCurrentRecord(null);
		}
	};

	const seekTo = (time: number) => {
		if (audioElement) {
			if (currentRecord?.voicemailRecordStarted) {
				audioElement.currentTime = time + currentRecord.voicemailRecordStarted;
			} else {
				audioElement.currentTime = time;
			}
			setCurrentTime(time);
		}
	};

	const downloadAudio = () => {
		if (currentAudioUrl) {
			const link = document.createElement('a');
			link.href = currentAudioUrl;
			const fileName = `${type === ECallRecordingType.VOICEMAIL ? ECallRecordingType.VOICEMAIL : ECallRecordingType.CALL}_${currentRecord?.callSid || 'recording'}.mp3`;
			link.download = fileName;
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
		}
	};

	return (
		<AudioPlayerContext.Provider
			value={{
				isPlaying,
				currentAudioUrl,
				currentTime,
				duration,
				audioSourceInfo,
				currentRecord,
				volume,
				setVolume,
				playbackSpeed,
				setPlaybackSpeed,
				playAudio,
				pauseAudio,
				stopAudio,
				seekTo,
				type,
				downloadAudio,
			}}
		>
			{children}
		</AudioPlayerContext.Provider>
	);
};

export const useAudioPlayer = () => {
	const context = useContext(AudioPlayerContext);
	if (context === undefined) {
		throw new Error('useAudioPlayer must be used within an AudioPlayerProvider');
	}
	return context;
};
